import { paymentMethodTerm } from "@billiard/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { OperatorIdentity } from "./api";
import {
  getRefundBusinessMetricDetail,
  refundBusinessMetricDetailQueryKey,
} from "./businessMetricDictionary";

export type RefundBusinessMetricDetailRoute =
  | { kind: "NONE" }
  | { kind: "INVALID" }
  | { kind: "DETAIL"; businessDate: string };

type RefundDetailLoader = typeof getRefundBusinessMetricDetail;

type RefundBusinessMetricDetailPanelProps = {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorIdentity["role"][];
  route: Exclude<RefundBusinessMetricDetailRoute, { kind: "NONE" }>;
  loader?: RefundDetailLoader;
};

function isCanonicalBusinessDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) && new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

export function refundBusinessMetricDetailRouteFromSearch(
  search: string,
): RefundBusinessMetricDetailRoute {
  const params = new URLSearchParams(search);
  const views = params.getAll("view");
  if (views.length === 0) return { kind: "NONE" };

  const businessDates = params.getAll("businessDate");
  if (
    [...params.entries()].length !== 2 ||
    views.length !== 1 ||
    views[0] !== "REFUND_DETAILS" ||
    businessDates.length !== 1
  ) {
    return { kind: "INVALID" };
  }
  const businessDate = businessDates[0] ?? "";
  return isCanonicalBusinessDate(businessDate)
    ? { kind: "DETAIL", businessDate }
    : { kind: "INVALID" };
}

function formatMoney(fen: number): string {
  return `¥${(fen / 100).toFixed(2)}`;
}

function formatTime(timestamp: string, timeZone: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "medium",
    timeStyle: "medium",
    hour12: false,
    timeZone,
  }).format(new Date(timestamp));
}

function AuthorizedRefundBusinessMetricDetail({
  operator,
  route,
  loader,
}: {
  operator: OperatorIdentity;
  route: Extract<RefundBusinessMetricDetailRoute, { kind: "DETAIL" }>;
  loader: RefundDetailLoader;
}): React.JSX.Element {
  const context = {
    tenantId: operator.tenantId,
    storeId: operator.storeId,
    operatorId: operator.id,
  };
  const detail = useQuery({
    queryKey: refundBusinessMetricDetailQueryKey(context, route.businessDate),
    queryFn: ({ signal }) => loader(route.businessDate, context, signal),
    refetchOnMount: "always",
    retry: false,
  });
  const responseMatchesSelection =
    detail.data?.tenantId === operator.tenantId &&
    detail.data.storeId === operator.storeId &&
    detail.data.businessDate === route.businessDate;
  const visibleDetail =
    !detail.isFetching && !detail.isError && responseMatchesSelection ? detail.data : undefined;
  const responseUnsafe =
    !detail.isFetching && detail.data !== undefined && !responseMatchesSelection;

  return (
    <main className="commercial-desk" aria-label="营业日退款完整明细">
      <header className="commerce-header">
        <div>
          <Link className="button secondary compact" to="/orders">
            返回订单页
          </Link>
          <small>{route.businessDate} · 完整退款账本</small>
          <h1>营业日退款明细</h1>
          <p>
            退款按实际发生的营业日单独列示，不会静默放回原订单营业日，也不会把营业实收悄悄抵消。请分别查看实收与退款。
          </p>
        </div>
      </header>

      {detail.isFetching ? (
        <output className="empty-state" aria-live="polite">
          正在核对 {route.businessDate} 的完整退款账本，确认前不会显示旧日期、旧账号或旧门店的金额。
        </output>
      ) : detail.isError || responseUnsafe ? (
        <div className="error-band" role="alert">
          <span>
            这项退款明细暂时无法安全读取，旧明细和金额已隐藏；只影响本项查看，不表示整套 ERP 离线。
          </span>
          <button
            className="button secondary compact"
            type="button"
            onClick={() => detail.refetch()}
          >
            重新读取本项退款
          </button>
        </div>
      ) : visibleDetail ? (
        <>
          <section className="summary-grid" aria-label="营业日退款汇总">
            <div>
              <span>退款合计</span>
              <strong>{formatMoney(visibleDetail.valueFen)}</strong>
            </div>
            <div>
              <span>退款条数</span>
              <strong>{visibleDetail.factCount} 条</strong>
            </div>
            <div>
              <span>最近生成时间</span>
              <strong>
                <time dateTime={visibleDetail.generatedAt}>
                  {formatTime(visibleDetail.generatedAt, visibleDetail.timeZone)}
                </time>
              </strong>
            </div>
          </section>

          {visibleDetail.items.length === 0 ? (
            <section className="empty-state" aria-label="营业日退款为空">
              <strong>该营业日没有退款记录</strong>
              <p>完整退款账本已核对；这里只表示退款为零，不代表当日营业实收为零。</p>
            </section>
          ) : (
            <section aria-label="营业日逐笔退款">
              <div className="commerce-section-title">
                <div>
                  <h2>逐笔退款账本</h2>
                  <p>以下记录以退款实际发生日为准，不在页面重新计算或拼接订单。</p>
                </div>
              </div>
              <div className="member-lifecycle-timeline-list">
                {visibleDetail.items.map((item) => (
                  <article key={item.refundNumber}>
                    <div>
                      <strong>退款号 {item.refundNumber}</strong>
                      <time dateTime={item.createdAt}>
                        {formatTime(item.createdAt, visibleDetail.timeZone)}
                      </time>
                    </div>
                    <dl>
                      <div>
                        <dt>原订单</dt>
                        <dd>
                          <Link
                            className="member-lifecycle-order-link"
                            to={`/orders?orderQuery=${encodeURIComponent(item.orderNumber)}`}
                          >
                            {item.orderNumber}
                          </Link>
                        </dd>
                      </div>
                      <div>
                        <dt>原订单营业日</dt>
                        <dd>{item.orderBusinessDate}</dd>
                      </div>
                      <div>
                        <dt>桌号</dt>
                        <dd>{item.tableNumber}</dd>
                      </div>
                      <div>
                        <dt>原付款方式 / 金额</dt>
                        <dd>
                          {paymentMethodTerm(item.paymentMethod).label} /{` `}
                          {formatMoney(item.originalPaymentAmountFen)}
                        </dd>
                      </div>
                      <div>
                        <dt>本次退款</dt>
                        <dd>{formatMoney(item.amountFen)}</dd>
                      </div>
                      <div>
                        <dt>退款原因</dt>
                        <dd>{item.reason}</dd>
                      </div>
                      <div>
                        <dt>经办 / 授权</dt>
                        <dd>
                          经办：{item.operatorDisplayName}；授权：{item.authorizerDisplayName}
                        </dd>
                      </div>
                      <div>
                        <dt>数据来源</dt>
                        <dd>门店退款记录</dd>
                      </div>
                    </dl>
                  </article>
                ))}
              </div>
            </section>
          )}
        </>
      ) : null}
    </main>
  );
}

export function RefundBusinessMetricDetailPanel({
  operator,
  effectiveRoles,
  route,
  loader = getRefundBusinessMetricDetail,
}: RefundBusinessMetricDetailPanelProps): React.JSX.Element {
  if (route.kind === "INVALID") {
    return (
      <main className="commercial-desk" aria-label="退款明细链接无效">
        <div className="error-band" role="alert">
          <span>退款明细的营业日期无法安全确认，系统没有读取或展示普通订单列表。</span>
          <Link className="button secondary compact" to="/orders">
            返回订单页
          </Link>
        </div>
      </main>
    );
  }

  const authorized = effectiveRoles.some((role) => role === "OWNER" || role === "MANAGER");
  if (!authorized) {
    return (
      <main className="commercial-desk" aria-label="退款明细无查看权限">
        <div className="error-band" role="alert">
          <span>当前账号不能查看全店退款明细，系统没有读取普通订单或退款金额。</span>
          <Link className="button secondary compact" to="/orders">
            返回订单页
          </Link>
        </div>
      </main>
    );
  }

  return (
    <AuthorizedRefundBusinessMetricDetail
      key={`${operator.tenantId}\u0000${operator.storeId}\u0000${operator.id}\u0000${route.businessDate}`}
      operator={operator}
      route={route}
      loader={loader}
    />
  );
}
